/**
 * Estado em memória da fila — única fonte de verdade no runtime.
 */

import { saveQueueToStorage } from '../storage/queueStore';
import type { SendEventParams } from '../../utils/sendEvent';
import type { QueueItem } from './types';
import {
  getQueueIndex,
  isQueueIndexEnabled,
  syncIndexFromArray,
} from './queueIndex';

let queue: QueueItem[] = [];
let maxQueueItemsLimit = 500;

function getMinimalConfig(config: SendEventParams['config']): {
  apiEndpoint: string;
  pixel: string;
  debug: boolean;
} {
  return {
    apiEndpoint: config.apiEndpoint,
    pixel: config.pixel,
    debug: config.debug ?? false,
  };
}

function syncFromIndex(): void {
  if (isQueueIndexEnabled()) {
    queue = getQueueIndex().toArray();
  }
}

export function getQueueSnapshot(): readonly QueueItem[] {
  return queue;
}

export function getQueueMutable(): QueueItem[] {
  return queue;
}

export function setQueue(items: QueueItem[]): void {
  queue = syncIndexFromArray(items);
}

export function replaceQueue(mutator: (current: QueueItem[]) => QueueItem[]): void {
  queue = syncIndexFromArray(mutator(queue));
}

export function pushQueueItem(item: QueueItem): void {
  queue.push(item);
  if (isQueueIndexEnabled()) getQueueIndex().add(item, queue.length - 1);
}

export function getQueueLength(): number {
  return queue.length;
}

export function findQueueIndex(id: string): number {
  if (isQueueIndexEnabled()) {
    return getQueueIndex().findArrayIndex(id);
  }
  return queue.findIndex(q => q.id === id);
}

export function resyncQueueIndexFromArray(): void {
  if (isQueueIndexEnabled()) {
    getQueueIndex().rebuild(queue);
  }
}

export function updateQueueItemAt(index: number, item: QueueItem): void {
  queue[index] = item;
  if (isQueueIndexEnabled()) {
    getQueueIndex().updateItem(item, index);
  }
}

export function removeQueueItemById(id: string): void {
  queue = queue.filter(q => q.id !== id);
  resyncQueueIndexFromArray();
}

export function persistQueue(): void {
  syncFromIndex();
  void saveQueueToStorage(
    queue.map(item => ({
      ...item,
      params: {
        ...item.params,
        config: getMinimalConfig(item.params.config),
      },
    }))
  ).catch(() => {
    /* falha de persistência não deve virar unhandled rejection */
  });
}

export function getMaxQueueItemsLimit(): number {
  return maxQueueItemsLimit;
}

export function setMaxQueueItemsLimit(limit: number): void {
  maxQueueItemsLimit = limit;
}

export function clearQueue(): void {
  queue = [];
  if (isQueueIndexEnabled()) getQueueIndex().rebuild([]);
}
